/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import {CreditItem} from "types/entities/PackagesItems";
import clsx from "clsx";
import FeatherIcon from "components/FeatherIcon";
import {currencyNumberFormat} from "utils/utility";
import {DEFAULT_STANDARD_CREDIT_PACKAGE_AMOUNT} from "helpers/config";
import {pricePerCredits} from "utils/creditsUtils";

interface BookingPackagesCreditsSelectStandardProps {
    sortedPackageCreditItems: CreditItem[];
    selectedPackageCreditItem: CreditItem | null;
    handlePackageCreditItemChange: (item: CreditItem | null) => void;
    isDisabled?: boolean;
    showLabels?: boolean;
}

const BookingPackagesCreditsSelectStandard = ({
    sortedPackageCreditItems,
    selectedPackageCreditItem,
    handlePackageCreditItemChange,
    isDisabled = false,
    showLabels = false,
}: BookingPackagesCreditsSelectStandardProps) => {
    const {t} = useTranslation();

    if (showLabels) {
        return (
            <div className={"BookingPackagesCreditsSelectStandardMobile flex flex-col md:grid grid-cols-4 gap-2 w-full items-end"}>
                {/* No Credits */}
                <button
                    type={"button"}
                    disabled={isDisabled}
                    onClick={() => handlePackageCreditItemChange(null)}
                    className={clsx(
                        "min-h-[56px] md:min-h-[80px] w-full flex flex-row items-center justify-center gap-2 md:gap-3 rounded-xl border-2 px-2 sm:px-4 py-3 transition-all duration-150",
                        selectedPackageCreditItem === null
                            ? "bg-[#D9D9D9] border-mid-grey text-mid-grey border-solid"
                            : "border-[#D9D9D9] hover:border-mid-grey hover:bg-white bg-[#FBFBFB] text-transparent border-dashed",
                        isDisabled && "cursor-not-allowed"
                    )}
                >
                    <div className={"border-2 rounded-full bg-white p-0.5"}>
                        <FeatherIcon
                            name={"check"}
                            className={clsx("size-4 sm:size-5 flex-none ", selectedPackageCreditItem === null ? "text-mid-grey" : "text-transparent")}
                            strokeWidth={3}
                        />
                    </div>
                    <div className={"flex flex-row md:flex-col gap-1 md:gap-0 w-full md:w-auto items-center"}>
                        <span
                            className={clsx(
                                "font-bold text-xs sm:text-sm flex-1 text-left md:text-center",
                                selectedPackageCreditItem === null ? "text-mid-grey" : "text-gray-500"
                            )}
                        >
                            {t("Keine Credits")}
                        </span>
                        <span
                            className={clsx(
                                "font-semibold text-[11px] xs:text-xs text-gray-500 shrink-0",
                                selectedPackageCreditItem === null ? "text-arbo-red" : "text-gray-500"
                            )}
                        >
                            {currencyNumberFormat(0)}
                        </span>
                    </div>
                </button>

                {sortedPackageCreditItems.map(item => {
                    const isSelected = selectedPackageCreditItem?.id === item.id;
                    const isRecommended = item.article.creditPackage?.amount_total === DEFAULT_STANDARD_CREDIT_PACKAGE_AMOUNT;

                    return (
                        <div key={item.id} className={"flex w-full flex-col"}>
                            {isRecommended && (
                                <div
                                    className={
                                        "rounded-t-xl flex flex-row w-full items-center justify-center gap-2 px-2 py-1.5 uppercase bg-[linear-gradient(90deg,#FFAE00_0%,#E1570B_35%,#D12C11_70%,#C20016_100%)] text-white"
                                    }
                                >
                                    <img src="/img/credits/top-recommendation-icon.svg" alt="top recommendation" className="size-[12px]" />
                                    <span className={"text-white text-[10px] font-bold leading-none"}>{t("Top recommendation")}</span>
                                </div>
                            )}
                            <button
                                type={"button"}
                                disabled={isDisabled}
                                onClick={() => handlePackageCreditItemChange(item)}
                                className={clsx(
                                    "min-h-[56px] md:min-h-[80px] w-full flex flex-row items-center justify-center gap-2 md:gap-3 border-2 px-2 sm:px-4 py-3 transition-all duration-150",
                                    isRecommended ? "rounded-b-xl border-t-0" : "rounded-xl",
                                    isSelected ? "bg-arbo-red/5 border-arbo-red" : "border-[#D9D9D9] bg-[#FBFBFB] hover:border-arbo-red",
                                    isDisabled && "cursor-not-allowed"
                                )}
                            >
                                <div className={"border-2 rounded-full bg-white p-0.5"}>
                                    <FeatherIcon
                                        name={"check"}
                                        className={clsx("size-3 xs:size-4 sm:size-5 flex-none ", isSelected ? "text-arbo-red" : "text-transparent")}
                                        strokeWidth={3}
                                    />
                                </div>
                                <div className={"flex flex-row md:flex-col gap-1 md:gap-0 w-full md:w-auto items-center"}>
                                    <span className={clsx("font-bold text-xs sm:text-sm flex-1 text-left", isSelected ? "text-arbo-red" : "text-black")}>
                                        {item.article?.creditPackage?.name ?? t("Unknown")}
                                    </span>
                                    <div className={"flex flex-col md:gap-1 text-right md:text-center"}>
                                        <span className={clsx("font-semibold text-[11px] xs:text-xs shrink-0", isSelected ? "text-arbo-red" : "text-black")}>
                                            {currencyNumberFormat(item.price)}
                                        </span>
                                        <span className={"font-medium text-[8px]"}>
                                            {currencyNumberFormat(pricePerCredits(item.article?.creditPackage?.amount_total ?? 0))} {t("per credit")}
                                        </span>
                                    </div>
                                </div>
                            </button>
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <div className={"BookingPackagesCreditsSelectStandard flex flex-row justify-start lg:justify-center gap-2 lg:gap-1 xl:gap-2"}>
            <div className={"flex flex-col"}>
                <button
                    type={"button"}
                    disabled={isDisabled}
                    onClick={() => handlePackageCreditItemChange(null)}
                    className={clsx(
                        "w-auto min-w-[64px] min-h-[50px] h-full relative flex flex-col items-center justify-center rounded-xl transition-all duration-150 shadow-sm border-2 px-2 py-1.5",
                        selectedPackageCreditItem === null
                            ? "bg-[#D9D9D9] border-mid-grey text-mid-grey border-solid"
                            : "border-[#D9D9D9] hover:border-mid-grey hover:bg-white bg-gray-50 text-transparent border-dashed",
                        isDisabled && "cursor-not-allowed"
                    )}
                >
                    <FeatherIcon
                        name={"check"}
                        className={clsx("size-6 flex-none", selectedPackageCreditItem === null ? "opacity-100" : "opacity-0")}
                        strokeWidth={3}
                    />
                </button>
            </div>
            {sortedPackageCreditItems.map(item => {
                const isSelected = selectedPackageCreditItem?.id === item.id;

                return (
                    <div key={item.id} className={"flex flex-col relative"}>
                        <button
                            type={"button"}
                            disabled={isDisabled}
                            onClick={() => handlePackageCreditItemChange(item)}
                            className={clsx(
                                "w-auto min-w-[102px] lg:min-w-[120px] min-h-[46px] h-full relative flex flex-col items-center justify-center rounded-xl transition-all duration-150 shadow-sm  px-1.5 xl:px-2 py-1.5",
                                isSelected
                                    ? "bg-arbo-red/5 border-arbo-red text-arbo-red border-2"
                                    : "border-[#D9D9D9] hover:border-arbo-red hover:bg-white border bg-gray-50 text-transparent",
                                isDisabled && "cursor-not-allowed"
                            )}
                        >
                            <FeatherIcon name={"check"} className={clsx("size-6 flex-none", isSelected ? "opacity-100" : "opacity-0")} strokeWidth={3} />
                        </button>
                    </div>
                );
            })}
        </div>
    );
};

export default BookingPackagesCreditsSelectStandard;
